import { useEffect, useState } from 'react';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { deskApi } from '@/api';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { QuickActionGrid } from '@/components/ui/QuickActionGrid';
import { PageSection } from '@/components/ui/PageSection';
import type { DeskDashboardSummary } from '@/types/desk';
import { showApiError } from '@/utils/apiError';

const QUICK_ACTIONS = [
  { to: '/desk/inbox', label: 'Inbox', description: 'Unified conversations and replies' },
  { to: '/desk/tickets', label: 'Tickets', description: 'Assign, resolve, and track SLA' },
  { to: '/desk/knowledgebase', label: 'Knowledge base', description: 'Articles for agents and customers' },
  { to: '/desk/reports', label: 'Reports', description: 'Volume, SLA, and agent metrics' },
];

export function DeskDashboardPage() {
  const [stats, setStats] = useState<DeskDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    deskApi
      .dashboardSummary()
      .then(setStats)
      .catch((err) => showApiError(err, 'Failed to load desk dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="Desk"
        title="Desk Dashboard"
        description="Support operations, SLA tracking, and ticket intelligence"
      >
        <DashboardSkeleton kpiCount={4} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      moduleLabel="Desk"
      title="Desk Dashboard"
      description="Support operations, SLA tracking, and ticket intelligence"
    >
      <KpiGrid columns={4}>
        <StatCard title="Total tickets" value={stats?.totalTickets ?? '—'} icon={<ConfirmationNumberIcon />} />
        <StatCard title="Unassigned" value={stats?.unassignedTickets ?? '—'} icon={<AssignmentIndIcon />} />
        <StatCard title="Open tickets" value={stats?.openTickets ?? '—'} icon={<HourglassTopIcon />} />
        <StatCard title="SLA breaches" value={stats?.breachedSlaTickets ?? '—'} icon={<WarningAmberIcon />} />
      </KpiGrid>

      <PageSection title="Quick actions">
        <QuickActionGrid items={QUICK_ACTIONS} columns={2} />
      </PageSection>
    </DashboardLayout>
  );
}
